import { EnemyContent, EnemyStatScalingModifier, WaveSpawnContent } from '../data';
import { createLogger, Logger } from '../utils/Logger';

export interface EnemyResolvedStats {
  readonly health: number;
  readonly maxHealth: number;
  readonly speed: number;
  readonly damage: number;
  readonly xpReward: number;
}

export interface EnemyScalingSummary {
  readonly enemyId: string;
  readonly waveIndex: number;
  readonly elapsedWaveTime: number;
  readonly legacyModifierIds: readonly string[];
  readonly timeMultipliers: Readonly<Record<'hp' | 'speed' | 'damage' | 'xpReward', number>>;
  readonly structuredScaling: WaveSpawnContent['scaling'];
  readonly resolvedStats: EnemyResolvedStats;
}

const LEGACY_MODIFIER_TABLE: Readonly<Record<string, Readonly<Record<'hp' | 'speed' | 'damage' | 'xpReward', number>>>> = {
  elite: { hp: 1.2, speed: 1.05, damage: 1.2, xpReward: 1.2 },
  swift: { hp: 1, speed: 1.2, damage: 1, xpReward: 1.05 },
  fortified: { hp: 1.35, speed: 0.9, damage: 1, xpReward: 1.15 },
  berserk: { hp: 0.95, speed: 1.1, damage: 1.35, xpReward: 1.2 }
};

export class EnemyScalingResolver {
  private readonly logger: Logger;

  constructor(logger: Logger = createLogger('EnemyScalingResolver')) {
    this.logger = logger;
  }

  public resolve(enemy: EnemyContent, spawn: WaveSpawnContent, waveIndex: number, elapsedWaveTime: number): EnemyScalingSummary {
    this.logger.debug('resolve:start', {
      enemyId: enemy.id,
      waveIndex,
      elapsedWaveTime,
      legacyModifierIds: spawn.modifiers,
      structuredScaling: spawn.scaling
    });

    let hp = enemy.stats.hp;
    let speed = enemy.stats.speed;
    let damage = enemy.stats.damage;
    let xpReward = enemy.stats.xpReward;

    for (const modifierId of spawn.modifiers) {
      const modifier = LEGACY_MODIFIER_TABLE[modifierId];
      if (!modifier) {
        this.logger.warn('resolve:unknown-legacy-modifier-ignored', {
          enemyId: enemy.id,
          modifierId,
          waveIndex
        });
        continue;
      }

      hp *= modifier.hp;
      speed *= modifier.speed;
      damage *= modifier.damage;
      xpReward *= modifier.xpReward;
    }

    const timeMultipliers = this.createTimeMultipliers(waveIndex, elapsedWaveTime);
    hp *= timeMultipliers.hp;
    speed *= timeMultipliers.speed;
    damage *= timeMultipliers.damage;
    xpReward *= timeMultipliers.xpReward;

    hp = this.applyStructuredScaling(hp, spawn.scaling?.hp);
    speed = this.applyStructuredScaling(speed, spawn.scaling?.speed);
    damage = this.applyStructuredScaling(damage, spawn.scaling?.damage);
    xpReward = this.applyStructuredScaling(xpReward, spawn.scaling?.xpReward);

    const resolvedStats: EnemyResolvedStats = {
      health: clampRoundInt(hp, 1),
      maxHealth: clampRoundInt(hp, 1),
      speed: roundFloat(Math.max(0, speed), 3),
      damage: clampRoundInt(damage, 0),
      xpReward: clampRoundInt(xpReward, 0)
    };

    const result: EnemyScalingSummary = {
      enemyId: enemy.id,
      waveIndex,
      elapsedWaveTime,
      legacyModifierIds: spawn.modifiers,
      timeMultipliers,
      structuredScaling: spawn.scaling,
      resolvedStats
    };

    this.logger.debug('resolve:complete', {
      enemyId: enemy.id,
      waveIndex,
      elapsedWaveTime,
      legacyModifierIds: spawn.modifiers,
      timeMultipliers,
      resolvedStats
    });

    return result;
  }

  private createTimeMultipliers(waveIndex: number, elapsedWaveTime: number): Readonly<Record<'hp' | 'speed' | 'damage' | 'xpReward', number>> {
    const safeWaveIndex = Math.max(0, waveIndex);
    const safeElapsed = Math.max(0, elapsedWaveTime);
    const timeTier = Math.floor(safeElapsed / 30);

    return {
      hp: 1 + safeWaveIndex * 0.06 + timeTier * 0.02,
      speed: 1 + safeWaveIndex * 0.015,
      damage: 1 + safeWaveIndex * 0.02,
      xpReward: 1 + safeWaveIndex * 0.03
    };
  }

  private applyStructuredScaling(
    value: number,
    scaling: EnemyStatScalingModifier | undefined
  ): number {
    if (!scaling) {
      return value;
    }

    const multiplier = typeof scaling.multiplier === 'number' && Number.isFinite(scaling.multiplier)
      ? scaling.multiplier
      : 1;
    const additive = typeof scaling.additive === 'number' && Number.isFinite(scaling.additive)
      ? scaling.additive
      : 0;

    return value * multiplier + additive;
  }
}

function clampRoundInt(value: number, min: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.max(min, Math.round(value));
}

function roundFloat(value: number, fractionDigits: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const factor = 10 ** fractionDigits;
  return Math.round(value * factor) / factor;
}
